import React, { useRef, useState, useEffect, useCallback } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
// @ts-ignore
import { useContentToken, EditableToken } from '@vibesites/runtime/react';
// @ts-ignore
import {
  HandHoldingHeartIcon,
  TableCellsLargeIcon,
  BullseyeIcon,
  CityIcon,
  HelmetSafetyIcon,
  WarehouseIcon,
} from '@vibesites/assets/icons/ui';

function parseStatValue(raw: string): { prefix: string; number: number; suffix: string } {
  const trimmed = String(raw).trim();
  const match = trimmed.match(/^([^0-9]*?)([\d,]+(?:\.\d+)?)(.*)$/);
  if (!match) return { prefix: '', number: 0, suffix: '' };
  const prefix = match[1];
  const number = parseFloat(match[2].replace(/,/g, ''));
  const suffix = match[3];
  return { prefix, number, suffix };
}

function formatNumber(n: number, hasDecimals: boolean): string {
  if (hasDecimals) {
    return n.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  }
  return Math.round(n).toLocaleString('en-US');
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

const ANIMATION_DURATION = 2000;

function AnimatedValue({ value }: { value: string }) {
  const { prefix, number: target, suffix } = parseStatValue(value);
  const hasDecimals = /\.\d/.test(String(value).replace(/,/g, ''));
  const [display, setDisplay] = useState(prefix + '0' + suffix);
  const hasAnimated = useRef(false);
  const elementRef = useRef<HTMLSpanElement>(null);

  const animate = useCallback(() => {
    if (target === 0) {
      setDisplay(value);
      return;
    }
    let start: number | null = null;
    const step = (timestamp: number) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      const progress = Math.min(elapsed / ANIMATION_DURATION, 1);
      const eased = easeOutCubic(progress);
      const current = eased * target;
      setDisplay(prefix + formatNumber(current, hasDecimals) + suffix);
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };
    requestAnimationFrame(step);
  }, [target, prefix, suffix, hasDecimals, value]);

  useEffect(() => {
    const el = elementRef.current;
    if (!el || hasAnimated.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true;
            observer.disconnect();
            animate();
          }
        }
      },
      { threshold: 0.2 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [animate]);

  useEffect(() => {
    if (hasAnimated.current) {
      setDisplay(value);
    }
  }, [value]);

  return <span ref={elementRef}>{display}</span>;
}

function StatCard({
  Icon,
  value,
  description,
}: {
  Icon: React.ComponentType<{ color: string }>;
  value: React.ReactNode;
  description: React.ReactNode;
}) {
  return (
    <div className="case-stat">
      <div className="case-stat-icon"><Icon color="currentColor" /></div>
      <div>
        <div className="case-stat-value-row">
          <span className="case-stat-icon-mobile"><Icon color="currentColor" /></span>
          {value}
        </div>
        {description}
      </div>
    </div>
  );
}

export function StatsRow() {
  const stat1Value = useContentToken('spacer.stat_1_value');
  const stat1Desc = useContentToken('spacer.stat_1_desc');
  const stat2Value = useContentToken('spacer.stat_2_value');
  const stat2Desc = useContentToken('spacer.stat_2_desc');
  const stat3Value = useContentToken('spacer.stat_3_value');
  const stat3Desc = useContentToken('spacer.stat_3_desc');
  const stat4Value = useContentToken('spacer.stat_4_value');
  const stat4Desc = useContentToken('spacer.stat_4_desc');
  const stat5Value = useContentToken('spacer.stat_5_value');
  const stat5Desc = useContentToken('spacer.stat_5_desc');
  const stat6Value = useContentToken('spacer.stat_6_value');
  const stat6Desc = useContentToken('spacer.stat_6_desc');

  return (
    <section className="case-stats">
      <div className="case-container">
        <div className="case-stats-grid">
          <StatCard
            Icon={HandHoldingHeartIcon}
            value={<EditableToken path="spacer.stat_1_value" as="div" className="case-stat-value" defaultValue={contentSchema.tokens['spacer.stat_1_value'].defaultValue}><AnimatedValue value={stat1Value || '50,000'} /></EditableToken>}
            description={<EditableToken path="spacer.stat_1_desc" as="div" className="case-stat-desc" defaultValue={contentSchema.tokens['spacer.stat_1_desc'].defaultValue}>{stat1Desc || 'Residents Served Daily'}</EditableToken>}
          />
          <StatCard
            Icon={TableCellsLargeIcon}
            value={<EditableToken path="spacer.stat_2_value" as="div" className="case-stat-value" defaultValue={contentSchema.tokens['spacer.stat_2_value'].defaultValue}><AnimatedValue value={stat2Value || '30,000+'} /></EditableToken>}
            description={<EditableToken path="spacer.stat_2_desc" as="div" className="case-stat-desc" defaultValue={contentSchema.tokens['spacer.stat_2_desc'].defaultValue}>{stat2Desc || 'Apartment Homes'}</EditableToken>}
          />
          <StatCard
            Icon={BullseyeIcon}
            value={<EditableToken path="spacer.stat_3_value" as="div" className="case-stat-value" defaultValue={contentSchema.tokens['spacer.stat_3_value'].defaultValue}><AnimatedValue value={stat3Value || '40'} /></EditableToken>}
            description={<EditableToken path="spacer.stat_3_desc" as="div" className="case-stat-desc" defaultValue={contentSchema.tokens['spacer.stat_3_desc'].defaultValue}>{stat3Desc || 'Years Providing Apartment Homes'}</EditableToken>}
          />
          <StatCard
            Icon={CityIcon}
            value={<EditableToken path="spacer.stat_4_value" as="div" className="case-stat-value" defaultValue={contentSchema.tokens['spacer.stat_4_value'].defaultValue}><AnimatedValue value={stat4Value || '8'} /></EditableToken>}
            description={<EditableToken path="spacer.stat_4_desc" as="div" className="case-stat-desc" defaultValue={contentSchema.tokens['spacer.stat_4_desc'].defaultValue}>{stat4Desc || 'Commercial Properties'}</EditableToken>}
          />
          <StatCard
            Icon={HelmetSafetyIcon}
            value={<EditableToken path="spacer.stat_5_value" as="div" className="case-stat-value" defaultValue={contentSchema.tokens['spacer.stat_5_value'].defaultValue}><AnimatedValue value={stat5Value || '20,000+'} /></EditableToken>}
            description={<EditableToken path="spacer.stat_5_desc" as="div" className="case-stat-desc" defaultValue={contentSchema.tokens['spacer.stat_5_desc'].defaultValue}>{stat5Desc || 'Units Built Over Two Decades'}</EditableToken>}
          />
          <StatCard
            Icon={WarehouseIcon}
            value={<EditableToken path="spacer.stat_6_value" as="div" className="case-stat-value" defaultValue={contentSchema.tokens['spacer.stat_6_value'].defaultValue}><AnimatedValue value={stat6Value || '800,000+'} /></EditableToken>}
            description={<EditableToken path="spacer.stat_6_desc" as="div" className="case-stat-desc" defaultValue={contentSchema.tokens['spacer.stat_6_desc'].defaultValue}>{stat6Desc || 'Square Feet of Commercial Property'}</EditableToken>}
          />
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Spacer',
  displayName: 'Stats Row',
  tokens: {
    'spacer.stat_1_value': {
      label: 'Stat 1 Value',
      type: 'text',
      defaultValue: '50,000',
    },
    'spacer.stat_1_desc': {
      label: 'Stat 1 Description',
      type: 'text',
      defaultValue: 'Residents Served Daily',
    },
    'spacer.stat_2_value': {
      label: 'Stat 2 Value',
      type: 'text',
      defaultValue: '30,000+',
    },
    'spacer.stat_2_desc': {
      label: 'Stat 2 Description',
      type: 'text',
      defaultValue: 'Apartment Homes',
    },
    'spacer.stat_3_value': {
      label: 'Stat 3 Value',
      type: 'text',
      defaultValue: '40',
    },
    'spacer.stat_3_desc': {
      label: 'Stat 3 Description',
      type: 'text',
      defaultValue: 'Years Providing Apartment Homes',
    },
    'spacer.stat_4_value': {
      label: 'Stat 4 Value',
      type: 'text',
      defaultValue: '8',
    },
    'spacer.stat_4_desc': {
      label: 'Stat 4 Description',
      type: 'text',
      defaultValue: 'Commercial Properties',
    },
    'spacer.stat_5_value': {
      label: 'Stat 5 Value',
      type: 'text',
      defaultValue: '20,000+',
    },
    'spacer.stat_5_desc': {
      label: 'Stat 5 Description',
      type: 'text',
      defaultValue: 'Units Built Over Two Decades',
    },
    'spacer.stat_6_value': {
      label: 'Stat 6 Value',
      type: 'text',
      defaultValue: '800,000+',
    },
    'spacer.stat_6_desc': {
      label: 'Stat 6 Description',
      type: 'text',
      defaultValue: 'Square Feet of Commercial Property',
    },
  },
};

(StatsRow as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default StatsRow;
